/**
 * Fin d'un défi privé (migration 0722) : l'avis sur le défi (1 à 5 étoiles + un mot)
 * et les SKILLCARDS sur lesquelles portaient les questions — c'est le CTA du défi :
 * « S'entraîner » sur une carte ouverte, « Débloquer » sur les autres (page d'achat).
 * Un invité voit les cartes et peut créer son compte pour s'entraîner.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Lock, Sparkles, Star } from 'lucide-react';
import { MathText } from '@jeraisonne/ui';
import { OrigamiCreature, formatCardLevels } from '@jeraisonne/ui/tool-cards';
import {
  CARD_RARITY_LABELS,
  CARD_RARITY_STARS,
  cardPalette,
  type CardColorTheme,
  type CardRarity,
} from '@jeraisonne/db/cards';
import { ArenaPanel } from '@/features/live-ui/live-arena';
import {
  reviewLabel,
  skillcardsToOffer,
  usePrivateLiveSkillcards,
  useRatePrivateLive,
  type PrivateLiveCard,
} from '@/lib/private-live';

export function PrivateLiveOutro({
  challengeId,
  isGuest,
}: {
  challengeId: string;
  isGuest: boolean;
}) {
  const data = usePrivateLiveSkillcards(challengeId);
  const cards = skillcardsToOffer(data.data?.cards ?? []);
  return (
    <div className="mt-6 space-y-4" data-testid="private-live-outro">
      <ReviewPanel challengeId={challengeId} initial={data.data?.review ?? null} />
      {cards.length > 0 && (
        <section aria-labelledby="pl-outro-cards" className="space-y-3">
          <div>
            <h2 id="pl-outro-cards" className="font-display text-lg font-extrabold text-white">
              Les SKILLCARDS du défi
            </h2>
            <p className="text-ink-600 text-sm">
              {data.data?.premium
                ? 'Entraîne-toi sur les notions du défi pour faire mieux la prochaine fois.'
                : 'Débloque-les pour t’entraîner sur tous leurs exercices et prendre ta revanche.'}
            </p>
          </div>
          <ul className="space-y-2">
            {cards.map((c) => (
              <li key={c.id}>
                <CardOffer card={c} isGuest={isGuest} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ReviewPanel({
  challengeId,
  initial,
}: {
  challengeId: string;
  initial: { stars: number; comment: string | null } | null;
}) {
  const rate = useRatePrivateLive(challengeId);
  const [stars, setStars] = useState<number>(initial?.stars ?? 0);
  const [comment, setComment] = useState(initial?.comment ?? '');
  const [sent, setSent] = useState(!!initial);
  // L'avis enregistré arrive après le 1er rendu : on le reprend tant qu'on n'a rien touché.
  const [touched, setTouched] = useState(false);
  if (!touched && initial && stars === 0) {
    setStars(initial.stars);
    setComment(initial.comment ?? '');
    setSent(true);
  }

  const send = (value: number, text: string) =>
    rate.mutate({ stars: value, comment: text.trim() || null }, { onSuccess: () => setSent(true) });

  return (
    <ArenaPanel className="space-y-3">
      <p className="font-display text-base font-bold text-white">Ton avis sur ce défi</p>
      <div role="radiogroup" aria-label="Note du défi" className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => {
          const on = n <= stars;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={n === stars}
              aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
              onClick={() => {
                setTouched(true);
                setStars(n);
                setSent(false);
                send(n, comment);
              }}
              className="flex h-11 w-11 items-center justify-center rounded-full active:scale-[0.95]"
            >
              <Star
                className={`h-7 w-7 ${on ? 'fill-[#f4c64e] text-[#f4c64e]' : 'text-[rgba(255,255,255,0.35)]'}`}
                aria-hidden
              />
            </button>
          );
        })}
        {stars > 0 && <span className="text-ink-600 ml-2 text-sm">{reviewLabel(stars)}</span>}
      </div>
      {stars > 0 && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            setTouched(true);
            send(stars, comment);
          }}
        >
          <label htmlFor="pl-review-comment" className="text-ink-600 block text-sm">
            Un mot pour l’organisateur ? (facultatif)
          </label>
          <textarea
            id="pl-review-comment"
            value={comment}
            maxLength={500}
            rows={2}
            onChange={(e) => {
              setTouched(true);
              setComment(e.target.value);
              setSent(false);
            }}
            className="w-full rounded-xl bg-[rgba(255,255,255,0.08)] px-3 py-2 text-sm text-white ring-1 ring-[rgba(255,255,255,0.15)] placeholder:text-[rgba(255,255,255,0.45)] focus:outline-none focus:ring-[#2f86ff]"
            placeholder="Trop bien, on refait ça vendredi !"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-[#34d399]" aria-live="polite">
              {sent ? 'Merci pour ton avis !' : ''}
            </span>
            <button
              type="submit"
              disabled={rate.isPending || sent}
              className="min-h-11 rounded-full bg-[#0566f2] px-5 text-sm font-bold text-white active:scale-[0.98] disabled:opacity-50"
            >
              {rate.isPending ? 'Envoi…' : 'Envoyer'}
            </button>
          </div>
          {rate.isError && (
            <p role="alert" className="text-sm text-[#fca5a5]">
              Ton avis n’a pas pu être envoyé. Réessaie.
            </p>
          )}
        </form>
      )}
    </ArenaPanel>
  );
}

function CardOffer({ card, isGuest }: { card: PrivateLiveCard; isGuest: boolean }) {
  const palette = cardPalette(card.levels, card.color_theme as CardColorTheme);
  const rarity = card.rarity as CardRarity;
  const stars = CARD_RARITY_STARS[rarity] ?? 1;
  return (
    <ArenaPanel className="flex items-center gap-3 p-3">
      <span
        aria-hidden
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-2"
        style={{ background: palette.bg, borderColor: palette.accent }}
      >
        <OrigamiCreature creature={card.creature} accent={palette.accent} className="h-9 w-9" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="line-clamp-2 block text-sm font-bold text-white">
          <MathText>{card.title}</MathText>
        </span>
        <span className="text-ink-600 flex items-center gap-1.5 text-xs">
          <span className="text-[#f4c64e]" aria-label={CARD_RARITY_LABELS[rarity] ?? ''}>
            {'★'.repeat(stars)}
          </span>
          <span className="truncate">
            {card.questions} question{card.questions > 1 ? 's' : ''} du défi
            {card.levels.length > 0 ? ` · ${formatCardLevels(card.levels)}` : ''}
          </span>
        </span>
      </span>
      {card.unlocked || isGuest ? (
        <Link
          to="/card/$cardId"
          params={{ cardId: card.id }}
          className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full bg-[rgba(255,255,255,0.12)] px-4 text-sm font-bold text-white active:scale-[0.98]"
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          {isGuest && !card.unlocked ? 'Voir' : 'S’entraîner'}
        </Link>
      ) : (
        <Link
          to="/card/$cardId/acheter"
          params={{ cardId: card.id }}
          search={{ return: `/card/${card.id}` }}
          className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full bg-[#f4c64e] px-4 text-sm font-bold text-[#10203f] active:scale-[0.98]"
        >
          <Lock className="h-4 w-4" aria-hidden />
          Débloquer
        </Link>
      )}
    </ArenaPanel>
  );
}
